import React, {useMemo} from "react";
import {Box, Typography} from "@mui/material";
import {IFlyerRuntime, IFlyerRuntimePage, IFlyerRuntimePageSpotPromo, ILayout} from "../../../../dtos/flyer";
import {isMobile} from "react-device-detect";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import {getHeightandWidthOfFlyerPage} from "./utils";
import {useSelector} from "react-redux";
import {IAppState} from "../../../../store/models";
import moment from "moment";
import {findCurrentTranslateLabel} from "../../../../com/globalsFunctions/globalFunctions";
import {FrameCoordinates} from "./pageFrame";

interface Props {
    layoutData: ILayout,
    page: IFlyerRuntimePage,
    promo: IFlyerRuntimePageSpotPromo,
    frameCoordinates: FrameCoordinates,
    framesWrapperRef?: any;
}

export const LayoutForFrame: React.FC<Props> = ({
                                                    layoutData,
                                                    page,
                                                    promo,
                                                    frameCoordinates,
                                                    framesWrapperRef
                                                }) => {
    const {isMultipage, isFullScreen} = useSelector(
        (state: IAppState) => state.controlsBranch
    );
    const flyerBranch = useSelector<IAppState, IFlyerRuntime | null>((state) => state.flyersBranch.data);
    const validToCaption = findCurrentTranslateLabel(196, 'Valid unlit date', flyerBranch);

    const {height: windowHeight, width: windowWidth} = useWindowDimensions();
    const {height, width} = useMemo(
        () =>
            getHeightandWidthOfFlyerPage(
                page,
                windowHeight,
                windowWidth,
                isMobile,
                isFullScreen
            ),
        [page, windowHeight, windowWidth, isMobile, isFullScreen]
    );
    const validFrom = moment(promo?.promoValidFromUtc).format(`DD.MM.YY`);
    const validTo = moment(promo?.promoValidToUtc).format(`DD.MM.YY`);
    const calculateSpotHeightAndWidth = ({
                                             X0PX,
                                             X1PX,
                                             Y0PX,
                                             Y1PX,
                                         }: any) => {
        const currentPageSelector = framesWrapperRef.current as HTMLElement;
        if (!currentPageSelector) {
            return {
                height: 0,
                width: 0,
                top: 0,
                left: 0,
            };
        }
        // const currentPageSelector = document.documentElement.querySelector(`.flyer_page_image_container${page.pageId}`);
        let elementHeight;
        let elementWidth;
        let top;
        let left;
        const constantHeight = page.heightPx / (Y1PX - Y0PX);
        const constantWidth = page.widthPx / (X1PX - X0PX);
        const paddingHeight = isFullScreen ? 106 : 200;

        const FLYER_PAPER_PAGE_WIDTH = page.widthPx;
        const FLYER_PAPER_PAGE_HEIGHT = page.heightPx;
        // const FLYER_PAPER_PAGE_WIDTH = 1388.0;
        // const FLYER_PAPER_PAGE_HEIGHT = 1772.0;

        // let pageWidth = $('.flyer_page_image_container').innerWidth();
        // let pageHeight = $('.flyer_page_image_container').innerHeight();

        // const currentPageSelector = $(
        //     `.${styles.flyerPageImageContainer}${page.pageId}`
        // );
        let pageWidth = currentPageSelector.clientWidth;
        let pageHeight = currentPageSelector.clientHeight;
        // let pageWidth = currentPageSelector.innerWidth();
        // let pageHeight = currentPageSelector.innerHeight();

        let scaleX = pageWidth! / FLYER_PAPER_PAGE_WIDTH;
        let scaleY = pageHeight! / FLYER_PAPER_PAGE_HEIGHT;

        if (isMobile) {
            elementWidth = (width - 10) / constantWidth;
            elementHeight = height / constantHeight;
        } else {
            elementWidth = width / constantWidth;
            elementHeight = (windowHeight - paddingHeight) / constantHeight;
        }

        if (isMultipage && isMobile) {
            top = Y0PX / ((Y1PX - Y0PX) / elementHeight) / 2;
            left = X0PX / ((X1PX - X0PX) / elementWidth) / 2;
            elementWidth = elementWidth / 2;
            elementHeight = elementHeight / 2;
        } else {
            // top = (spotY0PX / ((spotY1PX - spotY0PX) / spotHeight)) * zoomScale;
            // left = (spotX0PX / ((spotX1PX - spotX0PX) / spotWidth)) * zoomScale;

            // top = (spotY0PX / ((spotY1PX - spotY0PX) / spotHeight));
            // left = (spotX0PX / ((spotX1PX - spotX0PX) / spotWidth));

            left = X0PX * scaleX;
            top = Y0PX * scaleY;

            elementWidth = (X1PX - X0PX) * scaleX;
            elementHeight = (Y1PX - Y0PX) * scaleY;

            // spotHeight = spotHeight * zoomScale;
            // spotWidth = spotWidth * zoomScale;
        }


        return {
            height: elementHeight,
            width: elementWidth,
            top: top - 1,
            left: left - 1,
        };
    };

    const captionOneCoordinates =
        calculateSpotHeightAndWidth({
            X0PX: layoutData.caption1X0Px,
            X1PX: layoutData.caption1X1Px,
            Y0PX: layoutData.caption1Y0Px,
            Y1PX: layoutData.caption1Y1Px
        });
    const captionTwoCoordinates =
        calculateSpotHeightAndWidth({
            X0PX: layoutData.caption2X0Px,
            X1PX: layoutData.caption2X1Px,
            Y0PX: layoutData.caption2Y0Px,
            Y1PX: layoutData.caption2Y1Px
        });
    const imageCoordinates =
        calculateSpotHeightAndWidth({
            X0PX: layoutData.imageX0Px,
            X1PX: layoutData.imageX1Px,
            Y0PX: layoutData.imageY0Px,
            Y1PX: layoutData.imageY1Px
        });
    const priceBadgeCoordinates =
        calculateSpotHeightAndWidth({
            X0PX: layoutData.priceBadgeX0Px,
            X1PX: layoutData.priceBadgeX1Px,
            Y0PX: layoutData.priceBadgeY0Px,
            Y1PX: layoutData.priceBadgeY1Px
        });
    const validityFromCoordinates =
        calculateSpotHeightAndWidth({
            X0PX: layoutData.validityX0Px,
            X1PX: layoutData.validityX1Px,
            Y0PX: layoutData.validityY0Px,
            Y1PX: layoutData.validityY1Px
        });


    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    height: frameCoordinates.spotHeight,
                    width: frameCoordinates.spotWidth,
                    top: frameCoordinates.top,
                    left: frameCoordinates.left,
                    border: layoutData.borderColorRgba && layoutData.borderWeightPx ? `${layoutData.borderWeightPx}px solid ${layoutData.borderColorRgba}` : "2px solid transparent",
                    backgroundColor: layoutData.fillColorRgba ? layoutData.fillColorRgba : "transparent"
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        height: captionOneCoordinates.height,
                        width: captionOneCoordinates.width,
                        // top: captionOneCoordinates.top,
                        // left: captionOneCoordinates.left,
                        top: frameCoordinates.top >= captionOneCoordinates.top ? frameCoordinates.top - captionOneCoordinates.top : captionOneCoordinates.top - frameCoordinates.top,
                        left: frameCoordinates.left >= captionOneCoordinates.left ? frameCoordinates.left - captionOneCoordinates.left : captionOneCoordinates.left - frameCoordinates.left,
                        backgroundColor: layoutData.caption1FillColorRgba,
                    }}
                >
                    <Typography
                        sx={{
                            color: layoutData.caption1FontColorRgb,
                            fontFamily: layoutData.caption1Font.toLowerCase(),
                            fontSize: `${layoutData.caption1FontSizePx}px`,
                            direction: layoutData.textDir === 1 ? "ltr" : "rtl",
                            textOverflow: "ellipsis",
                            height: "100%",
                            whiteSpace: "inherit",
                        }}
                    >
                        {promo.promoDisplayCaption1}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        height: captionTwoCoordinates.height,
                        width: captionTwoCoordinates.width,
                        // top: captionTwoCoordinates.top,
                        // left: captionTwoCoordinates.left,
                        top: frameCoordinates.top >= captionTwoCoordinates.top ? frameCoordinates.top - captionTwoCoordinates.top : captionTwoCoordinates.top - frameCoordinates.top,
                        left: frameCoordinates.left >= captionTwoCoordinates.left ? frameCoordinates.left - captionTwoCoordinates.left : captionTwoCoordinates.left - frameCoordinates.left,
                        backgroundColor: layoutData.caption2FillColorRgba
                    }}
                >
                    <Typography
                        sx={{
                            color: layoutData.caption2FontColorRgb,
                            fontFamily: layoutData.caption2Font.toLowerCase(),
                            fontSize: `${layoutData.caption2FontSizePx}px`,
                            direction: layoutData.textDir === 1 ? "ltr" : "rtl",
                            textOverflow: "ellipsis",
                            height: "100%",
                            whiteSpace: "inherit",
                            // display: "-webkit-box",
                            // "WebkitLineClamp": 2,
                            // "WebkitBoxOrient": "vertical"
                        }}
                    >
                        {promo.promoDisplayCaption1}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        height: imageCoordinates.height,
                        width: imageCoordinates.width,
                        // top: imageCoordinates.top,
                        // left: imageCoordinates.left,
                        top: frameCoordinates.top >= imageCoordinates.top ? frameCoordinates.top - imageCoordinates.top : imageCoordinates.top - frameCoordinates.top,
                        left: frameCoordinates.left >= imageCoordinates.left ? frameCoordinates.left - imageCoordinates.left : imageCoordinates.left - frameCoordinates.left,
                    }}
                >
                    <img src={promo.promoImageThumbnail || ""}
                         style={{
                             maxWidth: `100%`,
                             maxHeight: `100%`,
                             height: `100%`,
                             width: `100%`,
                             objectFit: "contain",
                         }}
                         alt={"missing img"}/>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        height: priceBadgeCoordinates.height,
                        width: priceBadgeCoordinates.width,
                        // top: priceBadgeCoordinates.top,
                        // left: priceBadgeCoordinates.left,
                        top: frameCoordinates.top >= priceBadgeCoordinates.top ? frameCoordinates.top - priceBadgeCoordinates.top : priceBadgeCoordinates.top - frameCoordinates.top,
                        left: frameCoordinates.left >= priceBadgeCoordinates.left ? frameCoordinates.left - priceBadgeCoordinates.left : priceBadgeCoordinates.left - frameCoordinates.left,
                        backgroundColor: "red"
                    }}
                >
                    <Typography textAlign={"center"}>
                        Price Badge
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        height: validityFromCoordinates.height,
                        width: validityFromCoordinates.width,
                        // top: validityFromCoordinates.top,
                        // left: validityFromCoordinates.left,
                        backgroundColor: layoutData.validityFillColorRgba,
                        top: frameCoordinates.top >= validityFromCoordinates.top ? frameCoordinates.top - validityFromCoordinates.top : validityFromCoordinates.top - frameCoordinates.top,
                        left: frameCoordinates.left >= validityFromCoordinates.left ? frameCoordinates.left - validityFromCoordinates.left : validityFromCoordinates.left - frameCoordinates.left,
                    }}
                >
                    <Typography
                        sx={{
                            color: layoutData.validityFontColorRgb,
                            fontFamily: layoutData.validityFont.toLowerCase(),
                            fontSize: layoutData.validityFontSizePx,
                            textAlign: "center",
                            direction: layoutData.textDir === 1 ? "ltr" : "rtl",
                            textOverflow: "ellipsis",
                            height: "100%",
                            whiteSpace: "inherit",
                        }}
                    >
                        {
                            validFrom && validTo && validFrom !== validTo ? `${validFrom} - ${validTo}` : `${validToCaption} ${validTo}`
                        }
                    </Typography>
                </Box>
            </Box>
        </>
    )
}